<template>
  <div class="foodleap-dd antialiased pb-24">

    <DeepDiveDownloadModal v-if="$route.params.token"/>

    <!-- Hero Section -->
    <section class="pt-24 pb-0 dd-bg-light relative overflow-hidden">
      <div class="max-w-6xl mx-auto px-8 text-center relative z-10">

        <span class="dd-badge-green tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase animate-on-scroll">
          DEEP DIVE REPORTS
        </span>
        <h1 class="text-5xl md:text-6xl font-bold tracking-tight dd-text-slate mb-6 leading-[1.1] animate-on-scroll delay-1 mt-6">
          Strategic intelligence<br class="hidden md:block"> for food innovators
        </h1>
        <p class="text-xl dd-text-slate-70 font-light max-w-2xl mx-auto mb-8 animate-on-scroll delay-2">
          In-depth analyses of the transitions reshaping<br class="hidden md:block"> the future of food.
        </p>

        <!-- Topic pills -->
        <!--        <div class="tw-flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll delay-3">
                  <span v-for="tag in heroTags" :key="tag" class="dd-pill-tag px-5 py-2 rounded-full text-sm font-semibold">
                    {{ tag }}
                  </span>
                </div>-->

        <!-- Latest Deep Dive Card -->
        <div class="dd-latest-card animate-on-scroll text-left rounded-3xl overflow-hidden">
          <!-- Left: report info -->
          <div class="dd-latest-left p-12 lg:p-16 tw-flex flex-col justify-between">
            <div>
              <span class="dd-free-badge inline-block px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wide mb-6">FREE REPORT</span>
              <p class="dd-latest-label text-xs font-bold tracking-widest uppercase mb-4">LATEST DEEP DIVE</p>
              <h2 class="text-4xl lg:text-5xl font-bold dd-text-slate leading-tight mb-6">Balanced<br>Protein Futures</h2>
              <div class="dd-card-divider w-10 h-0.5 mb-8"></div>
              <h3 class="text-2xl dd-text-slate-60 font-medium mb-6">Structural shifts reshaping the protein transition.</h3>
              <p class="dd-text-slate-60 text-base font-light leading-relaxed mb-10 max-w-md">
                For years, the protein transition was framed as a story of replacement. New retail evidence suggests a more nuanced transition pathway is emerging.
              </p>
              <!-- Success state -->
              <div v-if="submitted" class="dd-success-state py-4">
                <div class="dd-success-icon w-12 h-12 rounded-full tw-flex items-center justify-center mb-4">
                  <Icon name="check-circle" size="24"/>
                </div>
                <h3 class="text-xl font-bold dd-text-slate mb-2">Check your inbox!</h3>
                <p class="dd-text-slate-60 text-base font-light">
                  An invite will be sent to <strong class="dd-text-slate font-bold">{{ email }}</strong> within a few minutes.
                </p>
              </div>

              <form v-else @submit.prevent="handleSubscribe" class="space-y-3">
                <div>
                  <input
                    type="email"
                    v-model="email"
                    placeholder="your@email.com"
                    class="dd-email-input w-full border rounded-xl p-4 text-base"
                    :class="{ 'dd-input-error': emailError }"
                    @focus="loadRecaptchaScript"
                  />
                  <span v-if="emailError" class="dd-error-text text-sm mt-1.5 block">{{ emailError }}</span>
                </div>
                <div v-if="errorMessage" class="tw-flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <span class="dd-error-text text-sm leading-relaxed">{{ errorMessage }}</span>
                </div>
                <button
                  type="submit"
                  class="dd-btn-register tw-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-xl font-bold text-base"
                  :disabled="submitting">
                  <span v-if="submitting">Sending...</span>
                  <span v-else>Send me the report →</span>
                </button>
                <p class="text-xs dd-text-slate-60 font-light">
                  Protected by reCAPTCHA —
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener" class="underline">Privacy</a> &amp;
                  <a href="https://policies.google.com/terms" target="_blank" rel="noopener" class="underline">Terms</a>. By clicking the button above, you agree with the <a
                  href="https://datascouts.eu/privacypolicy/" target="_blank">privacy policy</a> and <a
                  href="https://datascouts.eu/termsandconditions/" target="_blank">terms & conditions</a>.
                </p>
              </form>
            </div>
            <div class="mt-12 text-sm dd-text-slate-60 font-medium tw-flex gap-2 items-center">
              <span>May 2026</span>
              <span class="opacity-40">•</span>
              <span>Foodleap <span class="dd-text-green">×</span> DataScouts <span class="dd-text-green">×</span> Foodvalley</span>
            </div>
          </div>

          <!-- Right: report preview -->
          <div class="dd-latest-right relative overflow-hidden" style="min-height: 500px;">
            <img src="/images/static-home-page/foodleap/latest-report.png" alt="Report visualization" class="absolute inset-0 w-full h-full object-cover object-right opacity-80"/>
            <div class="absolute inset-0" style="background: linear-gradient(90deg, #1a1e3a 0%, #1a1e3a 40%, rgba(26,30,58,0.6) 100%);"></div>
            <div class="relative z-10 p-12 h-full tw-flex flex-col justify-center">
              <p class="dd-explores-label text-xs font-bold tracking-widest uppercase mb-8">THIS REPORT EXPLORES</p>
              <div class="space-y-8">
                <div class="tw-flex gap-4">
                  <div class="dd-explores-icon shrink-0 w-12 h-12 rounded-full tw-flex items-center justify-center">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold text-base">Retail signals</h4>
                    <p class="text-white/50 text-sm mt-0.5">Key patterns in Dutch retail data.</p>
                  </div>
                </div>
                <div class="tw-flex gap-4">
                  <div class="dd-explores-icon shrink-0 w-12 h-12 rounded-full tw-flex items-center justify-center">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold text-base">Product innovation</h4>
                    <p class="text-white/50 text-sm mt-0.5">How balanced protein products are evolving.</p>
                  </div>
                </div>
                <div class="tw-flex gap-4">
                  <div class="dd-explores-icon shrink-0 w-12 h-12 rounded-full tw-flex items-center justify-center">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold text-base">Strategic implications</h4>
                    <p class="text-white/50 text-sm mt-0.5">What this means for industry and investors.</p>
                  </div>
                </div>
                <div class="tw-flex gap-4">
                  <div class="dd-explores-icon shrink-0 w-12 h-12 rounded-full tw-flex items-center justify-center">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold text-base">Future outlook</h4>
                    <p class="text-white/50 text-sm mt-0.5">Emerging opportunities and challenges ahead.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Access all Deep Dive Reports -->
        <div class="dd-access-strip animate-on-scroll delay-1 mt-6 rounded-3xl tw-flex flex-col md:flex-row items-center justify-between px-8 py-8 gap-6">
          <div class="tw-flex items-center gap-6">
            <div class="dd-access-icon w-16 h-16 rounded-2xl tw-flex items-center justify-center shrink-0">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke-linecap="round"
                      stroke-linejoin="round" stroke-width="1.5"/>
              </svg>
            </div>
            <div class="text-left">
              <h3 class="font-bold dd-text-slate text-xl">Access all Deep Dive Reports</h3>
              <p class="text-sm dd-text-slate-60 mt-0.5">Become a member to unlock all current and upcoming reports.</p>
            </div>
          </div>
          <button @click="$router.push('/intelligence')" class="dd-btn-membership shrink-0 tw-flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-sm">
            Explore membership
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M14 5l7 7m0 0l-7 7m7-7H3" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
            </svg>
          </button>
        </div>

        <!-- Trusted by logos -->
        <div class="dd-trusted-section animate-on-scroll delay-2 pt-14 pb-16">
          <p class="dd-trusted-label text-xs font-bold tracking-widest uppercase mb-12">
            TRUSTED BY LEADING ORGANISATIONS IN THE FOOD ECOSYSTEM
          </p>
          <div class="tw-flex flex-wrap items-center justify-center gap-x-12 gap-y-6 dd-logos-wrapper">
            <div v-for="logo in trustedLogos" :key="logo.name" class="dd-logo-item">
              <img v-if="logo.image" :src="logo.image" :alt="logo.name" class="h-8 object-contain"/>
              <span v-else class="dd-logo-text">{{ logo.name }}</span>
            </div>
          </div>
        </div>

      </div>
      <div class="dd-blob absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-15"></div>
      <div class="dd-blob-2 absolute -bottom-20 -left-20 w-72 h-72 rounded-full blur-3xl opacity-12"></div>
    </section>

    <!-- Executive Summary -->
    <section class="py-20 bg-white">
      <div class="max-w-3xl mx-auto px-8">
        <div class="text-center mb-12 animate-on-scroll">
          <span class="dd-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6">
            SAMPLE REPORT
          </span>
          <h2 class="text-4xl font-bold dd-text-slate mb-3 mt-6">Executive Summary</h2>
          <p class="dd-text-slate-60 font-light">Alternative Proteins: Market Landscape 2025</p>
        </div>

        <div class="dd-exec-doc animate-on-scroll delay-1 relative">
          <!-- Document header -->
          <div class="dd-exec-header tw-flex items-center justify-between px-8 py-4 rounded-t-2xl">
            <div class="tw-flex items-center gap-3">
              <div class="dd-watermark-dot w-2 h-2 rounded-full"></div>
              <span class="text-xs font-black tracking-[0.2em] uppercase" style="color: rgba(33,36,74,0.35)">FOODLEAP DEEP DIVE</span>
            </div>
            <span class="dd-exec-tag text-xs font-bold px-2.5 py-1 rounded-full">Q1 2025</span>
          </div>

          <!-- Document body -->
          <div class="px-8 pt-8 pb-0">
            <div class="dd-exec-label text-xs font-black tracking-widest uppercase mb-6">01 — Executive Summary</div>

            <div class="space-y-5 dd-exec-body text-base leading-relaxed">
              <p>The protein transition is entering a new phase.</p>

              <p>The initial wave, driven by plant-based substitution, has delivered important innovation but is now reaching its limits. Growth in fully plant-based categories is
                slowing, as consumers remain unwilling to compromise on taste, price, and familiarity. At the same time, retail systems continue to prioritize predictability,
                margin, and rotation, limiting the scalability of products that do not fit existing category dynamics.</p>

              <p>As a result, the market is shifting away from replacement towards a more gradual and system-compatible approach.</p>

              <p>Balanced protein products, combining animal and alternative protein sources, are emerging as a key pathway. Rather than requiring consumers to change behavior,
                these products enable incremental improvement within familiar formats. Early market evidence shows that this approach aligns more effectively with both consumer
                expectations and retail requirements.</p>

              <p>Adoption is primarily driven by taste, price, and usability, often supported by "invisible" innovation, where product reformulation is not explicitly communicated.
                This allows change to occur without disrupting established consumption patterns.</p>

              <p>However, scaling this model remains challenging. The primary barrier is not a lack of innovation or demand, but the structure of the system itself. Retail
                procurement processes, category management, and data infrastructures are not designed to recognize or support gradual product transformation. As a result, balanced
                protein remains underrepresented in both shelf strategy and market data.</p>

              <p>The next phase of the protein transition will therefore be defined by alignment rather than disruption.</p>

              <p>Companies that succeed will focus on:</p>

              <ul class="dd-exec-list space-y-2 pl-0 ml-0">
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>reformulating high-volume products rather than creating entirely new categories</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>designing products that fit existing retail and consumer dynamics</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>leveraging hybrid models as a scalable pathway</span>
                </li>
                <li class="tw-flex items-start gap-3">
                  <span class="dd-exec-bullet shrink-0 mt-2"></span>
                  <span>aligning innovation with system constraints and incentives</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Fade overlay -->
          <div class="dd-exec-fade absolute bottom-0 left-0 right-0 h-48 rounded-b-2xl pointer-events-none"></div>

          <!-- Bottom padding to make room for overlay -->
          <div class="h-48 rounded-b-2xl"></div>
        </div>
      </div>
    </section>

    <!-- Topics we cover -->
    <section class="py-20 dd-bg-light-grey">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-14 animate-on-scroll">
          <h2 class="text-4xl font-bold dd-text-slate mb-4">Topics we cover</h2>
          <p class="dd-text-slate-60 text-lg font-light">The key learnings from a deep dive into the balanced protein landscape.</p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(topic, i) in topics"
            :key="i"
            class="dd-topic-card animate-on-scroll"
            :class="i % 3 === 1 ? 'delay-1' : i % 3 === 2 ? 'delay-2' : ''">
            <div class="dd-topic-icon w-11 h-11 rounded-xl tw-flex items-center justify-center mb-4">
              <Icon :name="topic.icon" size="20"/>
            </div>
            <h3 class="text-lg font-bold dd-text-slate mb-2">{{ topic.title }}</h3>
            <p class="dd-text-slate-60 text-sm font-light leading-relaxed">{{ topic.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-24 dd-bg-slate">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-10 animate-on-scroll">
          <h2 class="text-3xl font-bold text-white mb-3">Get full access</h2>
          <p class="text-white/50 font-light">Everything you need to follow the food system transition.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-6 items-center">
          <!-- Innovation Challenges -->
          <div class="dd-price-card rounded-2xl p-8 animate-on-scroll">
            <h3 class="text-base font-bold text-white mb-1">Innovation Challenges</h3>
            <p class="text-white/40 text-xs mb-6">Co-design open innovation with startups</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">€7,500</span>
              <span class="text-white/40 text-sm ml-1">/ challenge</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Ecosystem search</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Partner matchmaking</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Managed pilot design</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="dd-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">Contact us</button>
          </div>

          <!-- Ecosystem Insights (highlighted) -->
          <div class="dd-price-card dd-price-card-featured rounded-2xl p-10 relative animate-on-scroll delay-1">
            <div class="dd-popular-badge absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase">
              Most Popular
            </div>
            <h3 class="text-xl font-bold text-white text-center mb-1 mt-6">Ecosystem Insights</h3>
            <p class="text-white/40 text-sm text-center mb-8">All transition reports + live ecosystem data</p>
            <div class="text-center mb-8">
              <span class="text-5xl font-black text-white">€9,000</span>
              <span class="text-white/40 text-base ml-2">/ year</span>
            </div>
            <ul class="space-y-3 mb-10">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Pulse — monthly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Radar — quarterly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Deep Dive Reports — annual</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Full ecosystem data access</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="dd-check-icon shrink-0"/>
                <span class="text-white text-sm">Access to a transition event</span>
              </li>
            </ul>
            <button
              @click="$router.push(`/login?redirect_uri=${encodeURIComponent('/transition-reports/trial')}`)"
              class="dd-btn-blue w-full py-4 rounded-full font-bold text-base">
              Subscribe now
            </button>
          </div>

          <!-- Transition Advisory -->
          <div class="dd-price-card rounded-2xl p-8 animate-on-scroll delay-2">
            <h3 class="text-base font-bold text-white mb-1">Transition Advisory</h3>
            <p class="text-white/40 text-xs mb-6">Bespoke strategy for your journey</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">Project</span>
              <span class="text-white/40 text-sm ml-1">based</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Bespoke deep dives</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Strategy workshop</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="dd-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Executive coaching</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="dd-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">Talk to us</button>
          </div>
        </div>
      </div>
    </section>

    <!-- Report Switcher Bar -->
    <div class="dd-rs-bar fixed bottom-6 left-1/2 -translate-x-1/2 z-50 tw-flex items-center gap-1 px-2 py-2 rounded-full shadow-2xl">
      <button @click="$router.push('/transition-pulse-report')" class="dd-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="wave-square" size="13"/>
        Pulse
      </button>
      <button @click="$router.push('/innovation-radar-report')" class="dd-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="bullseye" size="13"/>
        Radar
      </button>
      <button @click="$router.push('/deep-dive-report')" class="dd-rs-tab dd-rs-active tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="file-text" size="13"/>
        Deep Dive
      </button>
    </div>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'
  import { requestDownloadable } from '@/api/downloadable'
  import DeepDiveDownloadModal from './DeepDiveDownloadPage.vue'

  export default defineComponent({
    name: 'StaticHomeFoodleapDeepDivePage',
    components: { SimplifiedFooterFoodleapNew, Icon, DeepDiveDownloadModal },

    data() {
      return {
        email: '',
        submitted: false,
        submitting: false,
        emailError: '',
        errorType: null,
        errorMessage: null,
        recaptchaSiteKey: (window as any).RECAPTCHA_V3_SITE_KEY || null,
        heroTags: [
          'Alternative Proteins',
          'Circular Packaging',
          'Fermentation',
          'Precision Ag',
        ],
        reportExplores: [
          { icon: 'line-graph', title: 'Retail signals', desc: 'Key patterns in Dutch retail data.' },
          { icon: 'cube', title: 'Product innovation', desc: 'How balanced protein products are evolving.' },
          { icon: 'bullseye', title: 'Strategic implications', desc: 'What this means for industry and investors.' },
          { icon: 'users', title: 'Future outlook', desc: 'Emerging opportunities and challenges ahead.' },
        ],
        trustedLogos: [
          { name: 'Foodvalley', image: '/images/logos/foodvalley.webp' },
          { name: 'DataScouts', image: '/images/dslogo.png' },
        ],
        topics: [
          {
            icon: 'vial',
            title: 'Balanced Protein Goes Mainstream',
            desc: 'Products blending animal, plant and alternative protein are a practical way to improve nutrition and sustainability without changing eating habits.',
          },
          {
            icon: 'sync',
            title: 'Reformulation Over New Launches',
            desc: 'The biggest impact comes from adapting high-volume products, not from creating entirely new categories.',
          },
          {
            icon: 'eco',
            title: 'Taste & Texture Decide Everything',
            desc: 'No matter the story, products only scale if they meet expectations on flavour, mouthfeel, and usability.',
          },
          {
            icon: 'building',
            title: 'Shelf Space Is the Real Battlefield',
            desc: 'Retail decisions on listing, rotation, and pricing determine what succeeds.',
          },
          {
            icon: 'heart-outline',
            title: '"Healthy" Is Being Redefined',
            desc: 'From ultra-processed debates to functional ingredients, health positioning is becoming as important as sustainability.',
          },
          {
            icon: 'circle-nodes',
            title: 'Integration Beats Disruption',
            desc: 'Solutions that fit existing processes, supply chains, and consumer habits scale faster than those that try to replace them.',
          },
        ],
      }
    },

    methods: {
      // Loaded on first interaction with the form rather than on mount: reCAPTCHA
      // sets its own identifiers, and a visitor who only reads the page should
      // not be handed them. Returns a promise so `getRecaptchaToken` can wait
      // for a load that is still in flight.
      loadRecaptchaScript(): Promise<void> {
        if (!this.recaptchaSiteKey) return Promise.resolve()
        if (this._recaptchaLoad) return this._recaptchaLoad

        this._recaptchaLoad = new Promise<void>((resolve, reject) => {
          const script = document.createElement('script')
          script.id = 'recaptcha-v3-script'
          script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaSiteKey}`
          script.async = true
          script.onload = () => resolve()
          script.onerror = () => {
            this._recaptchaLoad = null
            reject(new Error('Failed to load reCAPTCHA'))
          }
          document.head.appendChild(script)
        })

        return this._recaptchaLoad
      },

      removeRecaptchaScript() {
        const script = document.getElementById('recaptcha-v3-script')
        if (script) script.remove()
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge) (badge as HTMLElement).style.display = 'none'
      },

      async getRecaptchaToken(): Promise<string | null> {
        if (!this.recaptchaSiteKey) return null

        await this.loadRecaptchaScript()

        return new Promise((resolve) => {
          (window as any).grecaptcha.ready(() => {
            (window as any).grecaptcha
              .execute(this.recaptchaSiteKey, { action: 'subscribe_deep_dive' })
              .then(resolve)
              .catch(() => resolve(null))
          })
        })
      },

      async handleSubscribe() {
        this.emailError = ''
        this.errorType = null
        this.errorMessage = null

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!this.email || !emailRegex.test(this.email)) {
          this.emailError = 'Please enter a valid email address.'
          return
        }

        this.submitting = true

        try {
          const recaptchaToken = await this.getRecaptchaToken()
          await requestDownloadable(this.email, 'deep-dive-report', recaptchaToken ?? undefined)
          this.submitted = true
        } catch (err) {
          if (err?.errors?.recaptcha_token) {
            this.errorType = 'recaptcha'
            this.errorMessage = 'Security check failed. Please try again.'
          } else if (err?.error === 'already_downloaded') {
            this.errorType = 'already_downloaded'
            this.errorMessage = 'You have already downloaded this report. Each report link can only be used once.'
          } else if (err?.error === 'pending_link') {
            this.errorType = 'pending_link'
            this.errorMessage = 'A download link has already been sent to this email. Please check your inbox.'
          } else {
            this.errorType = 'generic'
            this.errorMessage = 'Something went wrong. Please try again later.'
          }
        } finally {
          this.submitting = false
        }
      },
    },

    mounted() {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
    },

    beforeUnmount() {
      if (this._observer) this._observer.disconnect()
      this.removeRecaptchaScript()
    },
  })
</script>

<style lang="scss">
  .grecaptcha-badge {
    visibility: hidden !important;
  }
</style>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-dd {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    // ── Brand color variables ──────────────────────────────────────────────────
    $ps: #21244A; // primary slate
    $pg: #5CCC61; // primary green
    $blg: #D9E2DA; // brand light grey
    $blue: #70C1FF; // secondary blue (accent for this page)
    $yel: #FFC83D; // secondary yellow
    $vg: #B5F167; // secondary vibrant green

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.delay-1 {
        transition-delay: 0.12s;
      }

      &.delay-2 {
        transition-delay: 0.24s;
      }

      &.delay-3 {
        transition-delay: 0.36s;
      }

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    }

    // ── Color utilities ────────────────────────────────────────────────────────
    .dd-text-slate {
      color: $ps;
    }

    .dd-text-slate-60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .dd-text-slate-70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .dd-bg-slate {
      background-color: $ps;
    }

    .dd-bg-light {
      background: radial-gradient(circle at top center, rgba(112, 193, 255, 0.08) 0%, #ffffff 70%);
    }

    .dd-bg-light-grey {
      background-color: rgba(217, 226, 218, 0.25);
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .dd-badge-green {
      background-color: rgba(92, 204, 97, 0.15);
      color: darken($pg, 8%);
      border: 1px solid rgba(92, 204, 97, 0.3);
    }

    .dd-badge {
      background-color: rgba(112, 193, 255, 0.15);
      color: darken($blue, 25%);
      border: 1px solid rgba(112, 193, 255, 0.3);
    }

    .dd-blob {
      background-color: $blue;
    }

    .dd-blob-2 {
      background-color: $vg;
    }

    .dd-pill-tag {
      background-color: rgba(112, 193, 255, 0.1);
      color: darken($blue, 20%);
      border: 1px solid rgba(112, 193, 255, 0.25);
      transition: transform 0.2s, background-color 0.2s;
      cursor: default;

      &:hover {
        transform: translateY(-2px);
        background-color: rgba(112, 193, 255, 0.2);
      }
    }

    // ── Latest Deep Dive card ──────────────────────────────────────────────────
    .dd-latest-card {
      display: grid;
      grid-template-columns: 1fr 1fr;
      border: 1px solid $blg;
      box-shadow: 0 24px 60px rgba(33, 36, 74, 0.1);
      background: #fff;

      @media (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }

    .dd-latest-right {
      background-color: $ps;
    }

    .dd-free-badge {
      background-color: rgba(92, 204, 97, 0.15);
      color: darken($pg, 8%);
      border: 1px solid rgba(92, 204, 97, 0.25);
    }

    .dd-latest-label {
      color: $blue;
      letter-spacing: 0.15em;
    }

    .dd-card-divider {
      background-color: $pg;
    }

    .dd-text-green {
      color: $pg;
    }

    .dd-btn-register {
      background-color: $pg;
      color: $ps;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(92, 204, 97, 0.35);
      }
    }

    .dd-explores-label {
      color: $pg;
      letter-spacing: 0.15em;
    }

    .dd-explores-icon {
      background-color: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      color: $pg;
    }

    // ── Access strip ───────────────────────────────────────────────────────────
    .dd-access-strip {
      background-color: rgba(217, 226, 218, 0.3);
      border: 1px solid $blg;
    }

    .dd-access-icon {
      background-color: rgba(92, 204, 97, 0.12);
      color: darken($pg, 8%);
    }

    .dd-btn-membership {
      border: 1.5px solid rgba(33, 36, 74, 0.2);
      color: $ps;
      background: #fff;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:hover {
        border-color: rgba(33, 36, 74, 0.45);
        box-shadow: 0 4px 12px rgba(33, 36, 74, 0.1);
      }
    }

    // ── Trusted by ─────────────────────────────────────────────────────────────
    .dd-trusted-label {
      color: rgba(33, 36, 74, 0.4);
      letter-spacing: 0.12em;
    }

    .dd-logos-wrapper {
      opacity: 0.6;
      filter: grayscale(1);
      transition: opacity 0.5s, filter 0.5s;

      &:hover {
        opacity: 1;
        filter: grayscale(0);
      }
    }

    .dd-logo-item {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .dd-logo-text {
      color: $ps;
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      font-size: 1rem;
      letter-spacing: -0.01em;
    }

    // ── Form ───────────────────────────────────────────────────────────────────
    .dd-email-input {
      color: $ps;
      border-color: $blg;
      background-color: #ffffff;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &::placeholder {
        color: rgba(33, 36, 74, 0.35);
      }

      &:focus {
        border-color: $blue;
        box-shadow: 0 0 0 3px rgba(112, 193, 255, 0.2);
      }

      &.dd-input-error {
        border-color: #EF374E;
        box-shadow: 0 0 0 3px rgba(239, 55, 78, 0.1);
      }
    }

    .dd-error-text {
      color: #EF374E;
    }

    .dd-btn-submit {
      background-color: $ps;
      color: #ffffff;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.2);
      }

      &:disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }
    }

    // ── Success state ──────────────────────────────────────────────────────────
    .dd-success-state {
      border: 1px solid rgba(92, 204, 97, 0.25);
      border-radius: 1.25rem;
      background-color: rgba(92, 204, 97, 0.04);
      padding: 2.5rem 2rem;
    }

    .dd-success-icon {
      background-color: rgba(92, 204, 97, 0.15);
      color: darken(#5CCC61, 8%);
    }

    // ── Report mockup ──────────────────────────────────────────────────────────
    .dd-report-mockup {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.05);
    }

    .dd-report-header {
      background-color: rgba(255, 255, 255, 0.04);
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    }

    .dd-report-label {
      background-color: rgba(112, 193, 255, 0.2);
      color: $blue;
    }

    .dd-report-topic-tag {
      color: $blue;
      letter-spacing: 0.15em;
    }

    .dd-watermark-dot {
      background-color: $pg;
    }

    .dd-report-fade {
      background: linear-gradient(to top, $ps 0%, transparent 100%);
    }

    // ── Executive Summary ──────────────────────────────────────────────────────
    .dd-exec-doc {
      border: 1px solid $blg;
      border-radius: 1.25rem;
      overflow: hidden;
      box-shadow: 0 20px 60px rgba(33, 36, 74, 0.07);
    }

    .dd-exec-header {
      background-color: rgba(217, 226, 218, 0.25);
      border-bottom: 1px solid $blg;
    }

    .dd-exec-tag {
      background-color: rgba(112, 193, 255, 0.15);
      color: darken($blue, 25%);
    }

    .dd-exec-label {
      color: $blue;
      letter-spacing: 0.15em;
    }

    .dd-exec-body {
      color: rgba(33, 36, 74, 0.8);
      font-weight: 300;

      p:first-child {
        font-size: 1.1rem;
        font-weight: 500;
        color: $ps;
      }
    }

    .dd-exec-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .dd-exec-bullet {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: $blue;
    }

    .dd-exec-fade {
      background: linear-gradient(to top, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0.6) 70%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;

      > * {
        pointer-events: auto;
      }
    }

    // ── Topic cards ────────────────────────────────────────────────────────────
    .dd-topic-card {
      background-color: #ffffff;
      border: 1px solid $blg;
      border-radius: 1.25rem;
      padding: 1.75rem;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.08);
        border-color: rgba(112, 193, 255, 0.35);
      }
    }

    .dd-topic-icon {
      background-color: rgba(112, 193, 255, 0.12);
      color: darken($blue, 20%);
    }

    // ── Pricing block ──────────────────────────────────────────────────────────
    .dd-price-card-featured {
      border-color: rgba(112, 193, 255, 0.4);
      box-shadow: 0 0 0 1px rgba(112, 193, 255, 0.2);
    }

    .dd-btn-ghost {
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.6);
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.5);
        color: rgba(255, 255, 255, 0.9);
      }
    }

    .dd-price-card {
      background-color: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .dd-popular-badge {
      background-color: $blue;
      color: $ps;
    }

    .dd-check-icon {
      color: $blue;
      flex-shrink: 0;
    }

    // ── Report switcher bar ────────────────────────────────────────────────────
    .dd-rs-bar {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .dd-rs-tab {
      color: rgba(255, 255, 255, 0.45);
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    .dd-rs-active {
      background-color: $blue;
      color: $ps !important;
    }

    // ── CTA button ─────────────────────────────────────────────────────────────
    .dd-btn-blue {
      background-color: $blue;
      color: $ps;
      font-weight: 700;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(112, 193, 255, 0.4);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }
  }
</style>
